import React, {
  startTransition,
  useEffect,
  useState,
} from "react";
import { apiFetch } from "../../config/api";
import "./index.css";

const INVENTORY_BROWSER_ROOT = "__root__";
const STOCK_LIMIT = 200;
const ITEM_LIMIT = 100;
const TRANSACTION_LIMIT = 20;
const LOW_STOCK_LIMIT = 24;

const createReceiptForm = () => ({
  item: "",
  location: "",
  quantity: "",
  unit_cost: "",
  reference_note: "",
});

const createTransferForm = () => ({
  item: "",
  from_location: "",
  to_location: "",
  quantity: "",
  reference_note: "",
});

const createCatalogItemForm = () => ({
  name: "",
  brand: "",
  unit: "EACH",
  low_stock_threshold: "",
  notes: "",
});

const formatQuantity = (value) => {
  if (value === null || value === undefined || value === "") {
    return "0.00";
  }

  return Number(value).toFixed(2);
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  return `$${Number(value).toFixed(2)}`;
};

const formatDateTime = (value) => {
  if (!value) {
    return "N/A";
  }

  return new Date(value).toLocaleString();
};

const getApiErrorMessage = (error, fallbackMessage) => {
  const payload = error?.response?.data;

  if (!payload) {
    return fallbackMessage;
  }

  if (typeof payload === "string") {
    return payload;
  }

  if (payload.detail) {
    return payload.detail;
  }

  if (payload.error) {
    return payload.error;
  }

  const fieldErrors = Object.entries(payload)
    .map(([field, value]) => {
      if (Array.isArray(value)) {
        return `${field}: ${value.join(" ")}`;
      }

      if (typeof value === "string") {
        return `${field}: ${value}`;
      }

      return "";
    })
    .filter(Boolean);

  return fieldErrors[0] || fallbackMessage;
};

const getInventoryCategoryTrail = (categoryId, categories) => {
  if (!categoryId) {
    return [];
  }

  const categoriesById = new Map(
    categories.map((category) => [String(category.id), category])
  );
  const trail = [];
  let currentCategory = categoriesById.get(String(categoryId));

  while (currentCategory) {
    trail.unshift(String(currentCategory.id));
    currentCategory = currentCategory.parent
      ? categoriesById.get(String(currentCategory.parent.id))
      : null;
  }

  return trail;
};

const buildInventoryBrowserColumns = (categories, items, categoryTrail) => {
  const categoriesByParent = new Map();
  const itemsByParent = new Map();

  categories.forEach((category) => {
    const parentKey = category.parent
      ? String(category.parent.id)
      : INVENTORY_BROWSER_ROOT;

    if (!categoriesByParent.has(parentKey)) {
      categoriesByParent.set(parentKey, []);
    }

    categoriesByParent.get(parentKey).push(category);
  });

  items.forEach((item) => {
    const parentKey = item.category
      ? String(item.category.id)
      : INVENTORY_BROWSER_ROOT;

    if (!itemsByParent.has(parentKey)) {
      itemsByParent.set(parentKey, []);
    }

    itemsByParent.get(parentKey).push(item);
  });

  const sortByName = (left, right) =>
    (left.name || "").localeCompare(right.name || "", undefined, {
      numeric: true,
      sensitivity: "base",
    });

  categoriesByParent.forEach((entries) => entries.sort(sortByName));
  itemsByParent.forEach((entries) => entries.sort(sortByName));

  const columns = [];
  let parentKey = INVENTORY_BROWSER_ROOT;
  let depth = 0;

  while (true) {
    const categoryEntries = categoriesByParent.get(parentKey) || [];
    const itemEntries = itemsByParent.get(parentKey) || [];
    const entries = [
      ...categoryEntries.map((category) => ({
        id: `category-${category.id}`,
        kind: "category",
        label: category.name,
        value: String(category.id),
      })),
      ...itemEntries.map((item) => ({
        id: `item-${item.id}`,
        kind: "item",
        label: item.name,
        value: String(item.id),
      })),
    ];

    if (!entries.length) {
      break;
    }

    columns.push({ depth, entries });

    const selectedCategoryId = categoryTrail[depth];
    if (!selectedCategoryId) {
      break;
    }

    parentKey = selectedCategoryId;
    depth += 1;
  }

  return columns;
};

const getInventoryEntryInitial = (label) =>
  (label || "?").trim().charAt(0).toUpperCase();

const collectDescendantCategoryIds = (categoryId, categories) => {
  if (!categoryId) {
    return new Set();
  }

  const descendants = new Set([String(categoryId)]);
  const queue = [String(categoryId)];

  while (queue.length) {
    const currentId = queue.shift();
    categories.forEach((category) => {
      if (String(category.parent?.id || "") === currentId) {
        const nextId = String(category.id);
        if (!descendants.has(nextId)) {
          descendants.add(nextId);
          queue.push(nextId);
        }
      }
    });
  }

  return descendants;
};

const InventoryPage = () => {
  const [inventory, setInventory] = useState({
    categories: [],
    items: [],
    locations: [],
    stock: [],
    lowStock: [],
    transactions: [],
  });
  const [summary, setSummary] = useState({
    totalItems: 0,
    activeLocations: 0,
    stockRows: 0,
    totalUnits: 0,
  });
  const [filters, setFilters] = useState({
    category: "",
    location: "",
    transactionType: "",
  });
  const [receiptForm, setReceiptForm] = useState(createReceiptForm);
  const [transferForm, setTransferForm] = useState(createTransferForm);
  const [catalogItemForm, setCatalogItemForm] = useState(createCatalogItemForm);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSubmittingReceipt, setIsSubmittingReceipt] = useState(false);
  const [isSubmittingTransfer, setIsSubmittingTransfer] = useState(false);
  const [isSubmittingCatalogItem, setIsSubmittingCatalogItem] = useState(false);
  const [isDownloading, setIsDownloading] = useState({
    stock: false,
    transactions: false,
  });
  const [error, setError] = useState("");
  const [workflowMessage, setWorkflowMessage] = useState({
    receipt: "",
    transfer: "",
    catalogItem: "",
  });
  const [workflowError, setWorkflowError] = useState({
    receipt: "",
    transfer: "",
    catalogItem: "",
  });
  const [refreshTick, setRefreshTick] = useState(0);
  const [selectedInventoryCategoryTrail, setSelectedInventoryCategoryTrail] =
    useState([]);
  const [selectedInventoryItemId, setSelectedInventoryItemId] = useState("");
  const [receiptCategoryTrail, setReceiptCategoryTrail] = useState([]);
  const [transferCategoryTrail, setTransferCategoryTrail] = useState([]);
  const [isCatalogLookupOpen, setIsCatalogLookupOpen] = useState(false);
  const [isStockByLocationOpen, setIsStockByLocationOpen] = useState(false);

  const buildStockParams = () => ({
    location: filters.location || undefined,
  });

  const buildTransactionParams = () => ({
    transaction_type: filters.transactionType || undefined,
    location: filters.location || undefined,
  });

  const buildPrintableParams = () => ({
    category:
      selectedInventoryCategoryTrail[selectedInventoryCategoryTrail.length - 1] ||
      undefined,
    location: filters.location || undefined,
    transaction_type: filters.transactionType || undefined,
  });

  useEffect(() => {
    let isMounted = true;

    const fetchInventory = async () => {
      const stockParams = {
        location: filters.location || undefined,
      };
      const transactionParams = {
        transaction_type: filters.transactionType || undefined,
        location: filters.location || undefined,
      };

      if (refreshTick === 0) {
        setIsLoading(true);
      } else {
        setIsRefreshing(true);
      }

      setError("");

      try {
        const [
          categoriesResponse,
          itemsResponse,
          locationsResponse,
          stockResponse,
          lowStockResponse,
          transactionsResponse,
        ] = await Promise.all([
          apiFetch("/api/inventory-categories/"),
          apiFetch("/api/inventory-items/"),
          apiFetch("/api/inventory-locations/"),
          apiFetch("/api/inventory-stock/", { params: stockParams }),
          apiFetch("/api/inventory-stock/low-stock/", {
            params: stockParams,
          }),
          apiFetch("/api/inventory-transactions/", {
            params: transactionParams,
          }),
        ]);

        if (!isMounted) {
          return;
        }

        const stockRows = Array.isArray(stockResponse.data) ? stockResponse.data : [];
        const lowStockRows = Array.isArray(lowStockResponse.data)
          ? lowStockResponse.data
          : [];
        const locations = Array.isArray(locationsResponse.data)
          ? locationsResponse.data
          : [];
        const items = Array.isArray(itemsResponse.data) ? itemsResponse.data : [];
        const transactions = Array.isArray(transactionsResponse.data)
          ? transactionsResponse.data
          : [];

        setInventory({
          categories: Array.isArray(categoriesResponse.data)
            ? categoriesResponse.data
            : [],
          items,
          locations,
          stock: stockRows,
          lowStock: lowStockRows,
          transactions,
        });
        setSummary({
          totalItems: items.length,
          activeLocations: locations.filter((location) => location.is_active).length,
          stockRows: stockRows.length,
          totalUnits: stockRows.reduce(
            (runningTotal, row) => runningTotal + Number(row.quantity_on_hand || 0),
            0
          ),
        });
      } catch (fetchError) {
        if (!isMounted) {
          return;
        }

        setError(getApiErrorMessage(fetchError, "Unable to load inventory data."));
      } finally {
        if (!isMounted) {
          return;
        }

        setIsLoading(false);
        setIsRefreshing(false);
      }
    };

    fetchInventory();

    return () => {
      isMounted = false;
    };
  }, [
    filters.location,
    filters.transactionType,
    refreshTick,
  ]);

  const updateFilter = (key, value) => {
    setFilters((currentFilters) => ({
      ...currentFilters,
      [key]: value,
    }));
  };

  const updateReceiptField = (key, value) => {
    setReceiptForm((currentForm) => ({
      ...currentForm,
      [key]: value,
    }));
  };

  const updateTransferField = (key, value) => {
    setTransferForm((currentForm) => ({
      ...currentForm,
      [key]: value,
    }));
  };

  const updateCatalogItemField = (key, value) => {
    setCatalogItemForm((currentForm) => ({
      ...currentForm,
      [key]: value,
    }));
  };

  const refreshInventory = () => {
    startTransition(() => {
      setRefreshTick((currentTick) => currentTick + 1);
    });
  };

  const downloadCsv = async (endpoint, params, key, fallbackMessage) => {
    setIsDownloading((currentState) => ({ ...currentState, [key]: true }));
    setError("");

    try {
      const response = await apiFetch(endpoint, {
        params,
        responseType: "blob",
      });
      const disposition = response.headers["content-disposition"] || "";
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
      const filename = filenameMatch?.[1] || `${key}-export.csv`;
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const downloadLink = document.createElement("a");
      downloadLink.href = blobUrl;
      downloadLink.setAttribute("download", filename);
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (downloadError) {
      setError(getApiErrorMessage(downloadError, fallbackMessage));
    } finally {
      setIsDownloading((currentState) => ({ ...currentState, [key]: false }));
    }
  };

  const handleExportStock = () =>
    downloadCsv(
      "/api/inventory-stock/export/",
      buildStockParams(),
      "stock",
      "Unable to export current stock."
    );

  const handleExportTransactions = () =>
    downloadCsv(
      "/api/inventory-transactions/export/",
      buildTransactionParams(),
      "transactions",
      "Unable to export transaction history."
    );

  const handleOpenPrintableReport = () => {
    const params = new URLSearchParams();
    Object.entries(buildPrintableParams()).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      }
    });

    const query = params.toString();
    const target = query
      ? `${window.location.origin}/inventory/report?${query}`
      : `${window.location.origin}/inventory/report`;
    window.open(target, "_blank", "noopener,noreferrer");
  };

  const activeItems = inventory.items.filter((item) => item.is_active);
  const activeLocations = inventory.locations.filter((location) => location.is_active);
  const warehouseLocations = activeLocations.filter(
    (location) => location.location_type === "WAREHOUSE"
  );
  const defaultReceivingLocation = warehouseLocations.find(
    (location) => location.is_default_receiving_location
  );
  const sourceStockRows = inventory.stock.filter(
    (stockRow) =>
      (!transferForm.item || String(stockRow.item?.id) === transferForm.item) &&
      (!transferForm.from_location ||
        String(stockRow.location?.id) === transferForm.from_location)
  );
  const sourceStockMatch = inventory.stock.find(
    (stockRow) =>
      String(stockRow.item?.id) === transferForm.item &&
      String(stockRow.location?.id) === transferForm.from_location
  );
  const selectedInventoryItem = inventory.items.find(
    (item) => String(item.id) === String(selectedInventoryItemId)
  );
  const selectedReceiptItem = inventory.items.find(
    (item) => String(item.id) === String(receiptForm.item)
  );
  const selectedTransferItem = inventory.items.find(
    (item) => String(item.id) === String(transferForm.item)
  );
  const selectedInventoryCategoryId =
    selectedInventoryCategoryTrail[selectedInventoryCategoryTrail.length - 1] || "";
  const selectedInventoryCategory = inventory.categories.find(
    (category) => String(category.id) === selectedInventoryCategoryId
  );
  const selectedInventoryCategoryHasActiveChildren = inventory.categories.some(
    (category) =>
      String(category.parent?.id || "") === selectedInventoryCategoryId &&
      category.active
  );
  const canAddCatalogItemToSelection =
    Boolean(selectedInventoryCategory) &&
    !selectedInventoryCategoryHasActiveChildren;
  const selectedCategoryDescendants = collectDescendantCategoryIds(
    selectedInventoryCategoryId,
    inventory.categories
  );
  const inventoryBrowserColumns = buildInventoryBrowserColumns(
    inventory.categories.filter((category) => category.active),
    activeItems,
    selectedInventoryCategoryTrail
  );
  const receiptBrowserColumns = buildInventoryBrowserColumns(
    inventory.categories.filter((category) => category.active),
    activeItems,
    receiptCategoryTrail
  );
  const transferBrowserColumns = buildInventoryBrowserColumns(
    inventory.categories.filter((category) => category.active),
    activeItems,
    transferCategoryTrail
  );

  const matchesBrowserSelection = (item) => {
    if (!item) {
      return false;
    }

    if (selectedInventoryItemId) {
      return String(item.id) === String(selectedInventoryItemId);
    }

    if (selectedCategoryDescendants.size) {
      return selectedCategoryDescendants.has(String(item.category?.id || ""));
    }

    return true;
  };

  const handleInventoryCategorySelect = (categoryId, depth) => {
    setSelectedInventoryCategoryTrail((currentTrail) => [
      ...currentTrail.slice(0, depth),
      String(categoryId),
    ]);
    setSelectedInventoryItemId("");
  };

  const handleInventoryCategoryBacktrack = (depth) => {
    setSelectedInventoryCategoryTrail((currentTrail) =>
      currentTrail.slice(0, depth)
    );
    setSelectedInventoryItemId("");
  };

  const handleInventoryItemSelect = (item) => {
    if (!item) {
      return;
    }

    setSelectedInventoryCategoryTrail(
      getInventoryCategoryTrail(item.category?.id, inventory.categories)
    );
    setSelectedInventoryItemId(String(item.id));
  };

  const clearInventorySelection = () => {
    setSelectedInventoryCategoryTrail([]);
    setSelectedInventoryItemId("");
    setWorkflowMessage((currentState) => ({ ...currentState, catalogItem: "" }));
    setWorkflowError((currentState) => ({ ...currentState, catalogItem: "" }));
  };

  const handleWorkflowCategorySelect = (setTrail, updateItem, categoryId, depth) => {
    setTrail((currentTrail) => [
      ...currentTrail.slice(0, depth),
      String(categoryId),
    ]);
    updateItem("");
  };

  const handleWorkflowCategoryBacktrack = (setTrail, updateItem, depth) => {
    setTrail((currentTrail) => currentTrail.slice(0, depth));
    updateItem("");
  };

  const handleWorkflowItemSelect = (setTrail, updateItem, item) => {
    if (!item) {
      return;
    }

    setTrail(getInventoryCategoryTrail(item.category?.id, inventory.categories));
    updateItem(String(item.id));
  };

  const renderInventoryItemBrowser = ({
    columns,
    selectedItem,
    selectedItemId,
    categoryTrail,
    onCategorySelect,
    onCategoryBacktrack,
    onItemSelect,
  }) => (
    <div className="inventory-browser inventory-browser--workflow">
      <div className="inventory-browser__header">
        <strong>Inventory item selected</strong>
        <span>
          Choose one main category, then keep drilling into subcategories until
          the final inventory item appears.
        </span>
      </div>

      <div className="inventory-browser__columns">
        {columns.length ? (
          columns.map((column, columnIndex) => (
            <section
              key={`workflow-inventory-column-${columnIndex}`}
              className={`inventory-browser__column${
                columnIndex < categoryTrail.length
                  ? " inventory-browser__column--initial-rail"
                  : ""
              }`}
            >
              <div className="inventory-browser__column-title">
                {columnIndex < categoryTrail.length
                  ? columnIndex === 0
                    ? "Main"
                    : `Sub ${columnIndex}`
                  : columnIndex === 0
                  ? "Main Category"
                  : `Subcategory ${columnIndex}`}
              </div>
              <div className="inventory-browser__list">
                {column.entries.map((entry) => {
                  const isInitialRail = columnIndex < categoryTrail.length;
                  const isActive =
                    entry.kind === "category"
                      ? categoryTrail[columnIndex] === entry.value
                      : String(selectedItemId) === entry.value;

                  return (
                    <button
                      key={entry.id}
                      type="button"
                      className={`inventory-browser__entry${
                        isActive ? " inventory-browser__entry--active" : ""
                      }${
                        isInitialRail ? " inventory-browser__entry--initial" : ""
                      }`}
                      title={entry.label}
                      aria-label={`Select ${entry.label}`}
                      onClick={() => {
                        if (entry.kind === "category") {
                          if (isInitialRail && isActive) {
                            onCategoryBacktrack(columnIndex);
                            return;
                          }

                          onCategorySelect(entry.value, columnIndex);
                          return;
                        }

                        onItemSelect(
                          activeItems.find((item) => String(item.id) === entry.value)
                        );
                      }}
                    >
                      <span className="inventory-browser__entry-label">
                        {isInitialRail
                          ? getInventoryEntryInitial(entry.label)
                          : entry.label}
                      </span>
                      <span className="inventory-browser__entry-affordance">
                        {entry.kind === "category" ? ">" : ""}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))
        ) : (
          <div className="inventory-browser__empty">
            No active inventory categories or items are available.
          </div>
        )}
      </div>

      <div className="inventory-browser__selection">
        {selectedItem ? (
          <>
            <strong>{selectedItem.name}</strong>
            <span>{selectedItem.display_path}</span>
          </>
        ) : (
          <span>
            No item selected yet. Continue through the category path to a final
            inventory item.
          </span>
        )}
      </div>
    </div>
  );

  const handleReceiptSubmit = async (event) => {
    event.preventDefault();
    setWorkflowMessage((currentState) => ({ ...currentState, receipt: "" }));
    setWorkflowError((currentState) => ({ ...currentState, receipt: "" }));

    if (!receiptForm.item) {
      setWorkflowError((currentState) => ({
        ...currentState,
        receipt: "Select an inventory item from the category browser.",
      }));
      return;
    }

    setIsSubmittingReceipt(true);

    try {
      const payload = {
        item: Number(receiptForm.item),
        quantity: receiptForm.quantity,
        unit_cost: receiptForm.unit_cost || null,
        location: receiptForm.location ? Number(receiptForm.location) : null,
        reference_note: receiptForm.reference_note,
      };

      const response = await apiFetch("/api/inventory-stock/receive/", {
        method: "POST",
        body: payload,
      });
      const stockLocation = response.data?.stock?.location?.name;
      const resolvedLocation =
        stockLocation ||
        defaultReceivingLocation?.name ||
        "the default receiving warehouse";

      setWorkflowMessage((currentState) => ({
        ...currentState,
        receipt: `${response.data?.detail || "Receipt saved."} ${formatQuantity(
          receiptForm.quantity
        )} units were received into ${resolvedLocation}.`,
      }));
      setReceiptForm(createReceiptForm());
      setReceiptCategoryTrail([]);
      refreshInventory();
    } catch (submitError) {
      setWorkflowError((currentState) => ({
        ...currentState,
        receipt: getApiErrorMessage(
          submitError,
          "Unable to record the inventory receipt."
        ),
      }));
    } finally {
      setIsSubmittingReceipt(false);
    }
  };

  const handleTransferSubmit = async (event) => {
    event.preventDefault();
    setWorkflowMessage((currentState) => ({ ...currentState, transfer: "" }));
    setWorkflowError((currentState) => ({ ...currentState, transfer: "" }));

    if (!transferForm.item) {
      setWorkflowError((currentState) => ({
        ...currentState,
        transfer: "Select an inventory item from the category browser.",
      }));
      return;
    }

    if (transferForm.from_location === transferForm.to_location) {
      setWorkflowError((currentState) => ({
        ...currentState,
        transfer: "Destination location must be different from the source location.",
      }));
      return;
    }

    setIsSubmittingTransfer(true);

    try {
      const payload = {
        item: Number(transferForm.item),
        from_location: Number(transferForm.from_location),
        to_location: Number(transferForm.to_location),
        quantity: transferForm.quantity,
        reference_note: transferForm.reference_note,
      };

      const response = await apiFetch("/api/inventory-stock/transfer/", {
        method: "POST",
        body: payload,
      });
      const sourceName = response.data?.source_stock?.location?.name || "source";
      const destinationName =
        response.data?.destination_stock?.location?.name || "destination";

      setWorkflowMessage((currentState) => ({
        ...currentState,
        transfer: `${response.data?.detail || "Transfer saved."} ${formatQuantity(
          transferForm.quantity
        )} units moved from ${sourceName} to ${destinationName}.`,
      }));
      setTransferForm(createTransferForm());
      setTransferCategoryTrail([]);
      refreshInventory();
    } catch (submitError) {
      setWorkflowError((currentState) => ({
        ...currentState,
        transfer: getApiErrorMessage(
          submitError,
          "Unable to complete the stock transfer."
        ),
      }));
    } finally {
      setIsSubmittingTransfer(false);
    }
  };

  const handleCatalogItemSubmit = async (event) => {
    event.preventDefault();
    setWorkflowMessage((currentState) => ({ ...currentState, catalogItem: "" }));
    setWorkflowError((currentState) => ({ ...currentState, catalogItem: "" }));

    if (!canAddCatalogItemToSelection || !selectedInventoryCategory) {
      setWorkflowError((currentState) => ({
        ...currentState,
        catalogItem:
          "Select a lowest-level category before adding a catalog item.",
      }));
      return;
    }

    const trimmedName = catalogItemForm.name.trim();
    if (!trimmedName) {
      setWorkflowError((currentState) => ({
        ...currentState,
        catalogItem: "Enter an item name.",
      }));
      return;
    }

    setIsSubmittingCatalogItem(true);

    try {
      const response = await apiFetch("/api/inventory-items/", {
        method: "POST",
        body: {
          category: Number(selectedInventoryCategory.id),
          name: trimmedName,
          unit: catalogItemForm.unit || "EACH",
          brand: catalogItemForm.brand.trim(),
          low_stock_threshold: catalogItemForm.low_stock_threshold || null,
          notes: catalogItemForm.notes.trim(),
          is_active: true,
        },
      });

      setInventory((currentInventory) => ({
        ...currentInventory,
        items: [...currentInventory.items, response.data],
      }));
      setSummary((currentSummary) => ({
        ...currentSummary,
        totalItems: currentSummary.totalItems + 1,
      }));
      setSelectedInventoryItemId(String(response.data.id));
      setCatalogItemForm(createCatalogItemForm());
      setWorkflowMessage((currentState) => ({
        ...currentState,
        catalogItem: `${response.data.name} was added to ${selectedInventoryCategory.path}.`,
      }));
    } catch (submitError) {
      setWorkflowError((currentState) => ({
        ...currentState,
        catalogItem: getApiErrorMessage(
          submitError,
          "Unable to add the catalog item."
        ),
      }));
    } finally {
      setIsSubmittingCatalogItem(false);
    }
  };

  const filteredStockRows = inventory.stock
    .filter((stockRow) => matchesBrowserSelection(stockRow.item))
    .slice(0, STOCK_LIMIT);
  const filteredItems = inventory.items
    .filter((item) => matchesBrowserSelection(item))
    .slice(0, ITEM_LIMIT);
  const recentTransactions = inventory.transactions
    .filter((transaction) => matchesBrowserSelection(transaction.item))
    .slice(0, TRANSACTION_LIMIT);
  const lowStockRows = inventory.lowStock
    .filter((stockRow) => matchesBrowserSelection(stockRow.item))
    .slice(0, LOW_STOCK_LIMIT);

  return (
    <div className="inventory-page">
      <div className="container-responsive">
        <div className="inventory-page__stats">
          <article className="inventory-stat-card">
            <span className="inventory-stat-card__label">Catalog items</span>
            <strong>{summary.totalItems}</strong>
          </article>
          <article className="inventory-stat-card">
            <span className="inventory-stat-card__label">Active locations</span>
            <strong>{summary.activeLocations}</strong>
          </article>
          <article className="inventory-stat-card">
            <span className="inventory-stat-card__label">Stock rows</span>
            <strong>{summary.stockRows}</strong>
          </article>
          <article className="inventory-stat-card inventory-stat-card--accent">
            <span className="inventory-stat-card__label">Units on hand</span>
            <strong>{formatQuantity(summary.totalUnits)}</strong>
          </article>
        </div>

        <section className="inventory-page__workflows">
          <article className="inventory-panel inventory-panel--workflow">
            <div className="inventory-panel__header inventory-panel__header--workflow">
              <div>
                <p className="inventory-panel__kicker">Warehouse intake</p>
                <h2>Receive stock</h2>
              </div>
              <button
                type="button"
                className="inventory-action-button inventory-action-button--secondary"
                onClick={refreshInventory}
                disabled={isRefreshing}
              >
                {isRefreshing ? "Refreshing..." : "Refresh"}
              </button>
            </div>
            <p className="inventory-panel__description">
              Receive newly ordered parts into a warehouse location. Leave the
              warehouse blank to use the default receiving location.
            </p>
            <form className="inventory-form" onSubmit={handleReceiptSubmit}>
              <div className="inventory-field">
                <label>Item</label>
                {renderInventoryItemBrowser({
                  columns: receiptBrowserColumns,
                  selectedItem: selectedReceiptItem,
                  selectedItemId: receiptForm.item,
                  categoryTrail: receiptCategoryTrail,
                  onCategorySelect: (categoryId, depth) =>
                    handleWorkflowCategorySelect(
                      setReceiptCategoryTrail,
                      (value) => updateReceiptField("item", value),
                      categoryId,
                      depth
                    ),
                  onCategoryBacktrack: (depth) =>
                    handleWorkflowCategoryBacktrack(
                      setReceiptCategoryTrail,
                      (value) => updateReceiptField("item", value),
                      depth
                    ),
                  onItemSelect: (item) =>
                    handleWorkflowItemSelect(
                      setReceiptCategoryTrail,
                      (value) => updateReceiptField("item", value),
                      item
                    ),
                })}
              </div>
              <div className="inventory-field">
                <label htmlFor="inventory-receipt-location">Warehouse</label>
                <select
                  id="inventory-receipt-location"
                  value={receiptForm.location}
                  onChange={(event) =>
                    updateReceiptField("location", event.target.value)
                  }
                >
                  <option value="">
                    {defaultReceivingLocation
                      ? `Default receiving: ${defaultReceivingLocation.name}`
                      : "Use configured default warehouse"}
                  </option>
                  {warehouseLocations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="inventory-form__row">
                <div className="inventory-field">
                  <label htmlFor="inventory-receipt-quantity">Quantity</label>
                  <input
                    id="inventory-receipt-quantity"
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={receiptForm.quantity}
                    onChange={(event) =>
                      updateReceiptField("quantity", event.target.value)
                    }
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="inventory-field">
                  <label htmlFor="inventory-receipt-cost">Unit cost</label>
                  <input
                    id="inventory-receipt-cost"
                    type="number"
                    min="0"
                    step="0.01"
                    value={receiptForm.unit_cost}
                    onChange={(event) =>
                      updateReceiptField("unit_cost", event.target.value)
                    }
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div className="inventory-field">
                <label htmlFor="inventory-receipt-note">Reference note</label>
                <textarea
                  id="inventory-receipt-note"
                  value={receiptForm.reference_note}
                  onChange={(event) =>
                    updateReceiptField("reference_note", event.target.value)
                  }
                  placeholder="PO number, vendor note, or receiving comment"
                  rows="3"
                />
              </div>
              {workflowError.receipt ? (
                <div className="inventory-inline-message inventory-inline-message--error">
                  {workflowError.receipt}
                </div>
              ) : null}
              {workflowMessage.receipt ? (
                <div className="inventory-inline-message inventory-inline-message--success">
                  {workflowMessage.receipt}
                </div>
              ) : null}
              <div className="inventory-form__actions">
                <button
                  type="submit"
                  className="inventory-action-button"
                  disabled={isSubmittingReceipt}
                >
                  {isSubmittingReceipt ? "Receiving..." : "Receive stock"}
                </button>
              </div>
            </form>
          </article>

          <article className="inventory-panel inventory-panel--workflow">
            <div className="inventory-panel__header inventory-panel__header--workflow">
              <div>
                <p className="inventory-panel__kicker">Field allocation</p>
                <h2>Transfer stock</h2>
              </div>
              <span>{sourceStockRows.length} matching stock rows</span>
            </div>
            <p className="inventory-panel__description">
              Move available quantity between warehouses, trucks, vans, and
              trailers. Transfer quantities are checked against the live stock
              ledger.
            </p>
            <form className="inventory-form" onSubmit={handleTransferSubmit}>
              <div className="inventory-field">
                <label>Item</label>
                {renderInventoryItemBrowser({
                  columns: transferBrowserColumns,
                  selectedItem: selectedTransferItem,
                  selectedItemId: transferForm.item,
                  categoryTrail: transferCategoryTrail,
                  onCategorySelect: (categoryId, depth) =>
                    handleWorkflowCategorySelect(
                      setTransferCategoryTrail,
                      (value) => updateTransferField("item", value),
                      categoryId,
                      depth
                    ),
                  onCategoryBacktrack: (depth) =>
                    handleWorkflowCategoryBacktrack(
                      setTransferCategoryTrail,
                      (value) => updateTransferField("item", value),
                      depth
                    ),
                  onItemSelect: (item) =>
                    handleWorkflowItemSelect(
                      setTransferCategoryTrail,
                      (value) => updateTransferField("item", value),
                      item
                    ),
                })}
              </div>
              <div className="inventory-form__row">
                <div className="inventory-field">
                  <label htmlFor="inventory-transfer-source">From</label>
                  <select
                    id="inventory-transfer-source"
                    value={transferForm.from_location}
                    onChange={(event) =>
                      updateTransferField("from_location", event.target.value)
                    }
                    required
                  >
                    <option value="">Select source location</option>
                    {activeLocations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="inventory-field">
                  <label htmlFor="inventory-transfer-destination">To</label>
                  <select
                    id="inventory-transfer-destination"
                    value={transferForm.to_location}
                    onChange={(event) =>
                      updateTransferField("to_location", event.target.value)
                    }
                    required
                  >
                    <option value="">Select destination location</option>
                    {activeLocations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="inventory-field">
                <label htmlFor="inventory-transfer-quantity">Quantity</label>
                <input
                  id="inventory-transfer-quantity"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={transferForm.quantity}
                  onChange={(event) =>
                    updateTransferField("quantity", event.target.value)
                  }
                  placeholder="0.00"
                  required
                />
              </div>
              <div className="inventory-field">
                <label htmlFor="inventory-transfer-note">Reference note</label>
                <textarea
                  id="inventory-transfer-note"
                  value={transferForm.reference_note}
                  onChange={(event) =>
                    updateTransferField("reference_note", event.target.value)
                  }
                  placeholder="Truck restock note or transfer reason"
                  rows="3"
                />
              </div>
              <div className="inventory-form__hint">
                {sourceStockMatch ? (
                  <>
                    Available at source:{" "}
                    <strong>
                      {formatQuantity(sourceStockMatch.quantity_on_hand)}
                    </strong>{" "}
                    units in {sourceStockMatch.location?.name}
                  </>
                ) : (
                  "Select an item and source location to see available quantity."
                )}
              </div>
              {workflowError.transfer ? (
                <div className="inventory-inline-message inventory-inline-message--error">
                  {workflowError.transfer}
                </div>
              ) : null}
              {workflowMessage.transfer ? (
                <div className="inventory-inline-message inventory-inline-message--success">
                  {workflowMessage.transfer}
                </div>
              ) : null}
              <div className="inventory-form__actions">
                <button
                  type="submit"
                  className="inventory-action-button"
                  disabled={isSubmittingTransfer}
                >
                  {isSubmittingTransfer ? "Transferring..." : "Transfer stock"}
                </button>
              </div>
            </form>
          </article>
        </section>

        <section className="inventory-page__filters">
          <div className="inventory-field">
            <label htmlFor="inventory-location-filter">Location</label>
            <select
              id="inventory-location-filter"
              value={filters.location}
              onChange={(event) => updateFilter("location", event.target.value)}
            >
              <option value="">All locations</option>
              {inventory.locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>
          <div className="inventory-field">
            <label htmlFor="inventory-transaction-type-filter">Recent activity</label>
            <select
              id="inventory-transaction-type-filter"
              value={filters.transactionType}
              onChange={(event) => updateFilter("transactionType", event.target.value)}
            >
              <option value="">All activity</option>
              <option value="RECEIPT">Receipts</option>
              <option value="TRANSFER">Transfers</option>
              <option value="JOB_PULL">Job pulls</option>
              <option value="JOB_RETURN">Job returns</option>
              <option value="ADJUSTMENT">Adjustments</option>
            </select>
          </div>
        </section>

        {error ? <div className="inventory-page__error">{error}</div> : null}
        {isLoading ? (
          <div className="inventory-page__loading">Loading inventory...</div>
        ) : null}

        {!isLoading ? (
          <>
            <section className="inventory-panel inventory-panel--table">
              <div className="inventory-panel__header">
                <div>
                  <p className="inventory-panel__kicker">Low-stock follow-up</p>
                  <h2>Low-stock items</h2>
                </div>
                <span>Showing {lowStockRows.length}</span>
              </div>
              {lowStockRows.length ? (
                <div className="inventory-low-stock-list">
                  {lowStockRows.map((stockRow) => (
                    <article key={stockRow.id} className="inventory-low-stock-card">
                      <div>
                        <strong>{stockRow.item?.name}</strong>
                        <p>{stockRow.item?.display_path || "No item path available"}</p>
                      </div>
                      <div className="inventory-low-stock-card__meta">
                        <span>{stockRow.location?.name || "Unassigned location"}</span>
                        <span>
                          Qty {formatQuantity(stockRow.quantity_on_hand)} / Threshold{" "}
                          {formatQuantity(stockRow.effective_low_stock_threshold)}
                        </span>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="inventory-empty">
                  No low-stock rows match the current filters.
                </div>
              )}
            </section>

            <section className="inventory-panel inventory-panel--table">
              <div className="inventory-panel__header">
                <div>
                  <p className="inventory-panel__kicker">Audit trail</p>
                  <h2>Recent inventory activity</h2>
                </div>
                <span>Newest first</span>
              </div>
              <div className="inventory-table-wrap">
                <table className="inventory-table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Type</th>
                      <th>Item</th>
                      <th>Movement</th>
                      <th>Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentTransactions.length ? (
                      recentTransactions.map((transaction) => (
                        <tr key={transaction.id}>
                          <td>{formatDateTime(transaction.created_at)}</td>
                          <td>{transaction.transaction_type}</td>
                          <td>{transaction.item?.name || "N/A"}</td>
                          <td>
                            {(transaction.from_location?.name || "N/A") +
                              " -> " +
                              (transaction.to_location?.name || "N/A")}
                          </td>
                          <td>{formatQuantity(transaction.quantity)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="inventory-empty inventory-empty--table">
                          No transaction history matches the current filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="inventory-catalog-section">
              <div className="inventory-catalog-section__header">
                <div>
                  <p className="inventory-panel__kicker">Catalog</p>
                  <h2>Catalog lookup</h2>
                </div>
                <div className="inventory-catalog-section__controls">
                  <div className="inventory-filter-readout">
                    {selectedInventoryItem ? (
                      selectedInventoryItem.display_path
                    ) : selectedInventoryCategoryId ? (
                      inventory.categories.find(
                        (category) =>
                          String(category.id) === selectedInventoryCategoryId
                      )?.path || "Category selected"
                    ) : (
                      "All categories"
                    )}
                  </div>
                  <button
                    type="button"
                    className={`inventory-icon-button${
                      isCatalogLookupOpen ? " inventory-icon-button--active" : ""
                    }`}
                    onClick={() =>
                      setIsCatalogLookupOpen((currentIsOpen) => !currentIsOpen)
                    }
                    aria-label={
                      isCatalogLookupOpen
                        ? "Hide catalog lookup"
                        : "Show catalog lookup"
                    }
                    aria-expanded={isCatalogLookupOpen}
                    title={
                      isCatalogLookupOpen
                        ? "Hide catalog lookup"
                        : "Show catalog lookup"
                    }
                  >
                    <span
                      className="inventory-icon-button__search"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </div>
            </section>

            {isCatalogLookupOpen ? (
              <div className="inventory-page__grid">
                <section className="inventory-panel inventory-panel--browser">
                <div className="inventory-panel__header">
                  <div>
                    <p className="inventory-panel__kicker">Catalog</p>
                    <h2>Inventory item selected</h2>
                  </div>
                  <div className="inventory-browser-panel__actions">
                    <span>{inventory.categories.length} categories</span>
                    <button
                      type="button"
                      className="inventory-action-button inventory-action-button--secondary"
                      onClick={clearInventorySelection}
                      disabled={
                        !selectedInventoryItemId &&
                        selectedInventoryCategoryTrail.length === 0
                      }
                    >
                      Clear selection
                    </button>
                  </div>
                </div>
                <p className="inventory-panel__description">
                  Choose a top-level category, move through subcategories, and
                  stop when the exact inventory item is selected.
                </p>
                <div className="inventory-browser inventory-browser--inventory-page">
                  <div className="inventory-browser__columns">
                    {inventoryBrowserColumns.length ? (
                      inventoryBrowserColumns.map((column, columnIndex) => (
                        <section
                          key={`inventory-column-${columnIndex}`}
                          className={`inventory-browser__column${
                            columnIndex < selectedInventoryCategoryTrail.length
                              ? " inventory-browser__column--initial-rail"
                              : ""
                          }`}
                        >
                          <div className="inventory-browser__column-title">
                            {columnIndex < selectedInventoryCategoryTrail.length
                              ? columnIndex === 0
                                ? "Main"
                                : `Sub ${columnIndex}`
                              : columnIndex === 0
                              ? "Main Category"
                              : `Subcategory ${columnIndex}`}
                          </div>
                          <div className="inventory-browser__list">
                            {column.entries.map((entry) => {
                              const isInitialRail =
                                columnIndex < selectedInventoryCategoryTrail.length;
                              const isActive =
                                entry.kind === "category"
                                  ? selectedInventoryCategoryTrail[columnIndex] ===
                                    entry.value
                                  : String(selectedInventoryItemId) === entry.value;

                              return (
                                <button
                                  key={entry.id}
                                  type="button"
                                  className={`inventory-browser__entry${
                                    isActive
                                      ? " inventory-browser__entry--active"
                                      : ""
                                  }${
                                    isInitialRail
                                      ? " inventory-browser__entry--initial"
                                      : ""
                                  }`}
                                  title={entry.label}
                                  aria-label={`Select ${entry.label}`}
                                  onClick={() => {
                                    if (entry.kind === "category") {
                                      if (isInitialRail && isActive) {
                                        handleInventoryCategoryBacktrack(
                                          columnIndex
                                        );
                                        return;
                                      }

                                      handleInventoryCategorySelect(
                                        entry.value,
                                        columnIndex
                                      );
                                      return;
                                    }

                                    handleInventoryItemSelect(
                                      activeItems.find(
                                        (item) => String(item.id) === entry.value
                                      )
                                    );
                                  }}
                                >
                                  <span className="inventory-browser__entry-label">
                                    {isInitialRail
                                      ? getInventoryEntryInitial(entry.label)
                                      : entry.label}
                                  </span>
                                  <span className="inventory-browser__entry-affordance">
                                    {entry.kind === "category" ? ">" : ""}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </section>
                      ))
                    ) : (
                      <div className="inventory-browser__empty">
                        No active inventory categories or items are available.
                      </div>
                    )}
                  </div>

                  <div className="inventory-browser__selection">
                    {selectedInventoryItem ? (
                      <>
                        <strong>{selectedInventoryItem.name}</strong>
                        <span>{selectedInventoryItem.display_path}</span>
                      </>
                    ) : selectedInventoryCategoryId ? (
                      <>
                        <strong>
                          {inventory.categories.find(
                            (category) =>
                              String(category.id) === selectedInventoryCategoryId
                          )?.name || "Category selected"}
                        </strong>
                        <span>
                          {
                            inventory.categories.find(
                              (category) =>
                                String(category.id) === selectedInventoryCategoryId
                            )?.path
                          }
                        </span>
                      </>
                    ) : (
                      <span>
                        No item selected yet. Use the column browser to drill
                        into the inventory catalog.
                      </span>
                    )}
                  </div>

                  <form
                    className="inventory-add-item"
                    onSubmit={handleCatalogItemSubmit}
                  >
                    <div className="inventory-add-item__header">
                      <div>
                        <p className="inventory-panel__kicker">Add catalog item</p>
                        <h3>New final item</h3>
                      </div>
                      <span>
                        {canAddCatalogItemToSelection
                          ? selectedInventoryCategory?.path
                          : "Select a lowest-level category"}
                      </span>
                    </div>
                    <div className="inventory-form__row">
                      <div className="inventory-field">
                        <label htmlFor="inventory-new-item-name">Item name</label>
                        <input
                          id="inventory-new-item-name"
                          value={catalogItemForm.name}
                          onChange={(event) =>
                            updateCatalogItemField("name", event.target.value)
                          }
                          placeholder="Example: 1/2 in coupling"
                          disabled={!canAddCatalogItemToSelection}
                          required
                        />
                      </div>
                      <div className="inventory-field">
                        <label htmlFor="inventory-new-item-brand">Brand</label>
                        <input
                          id="inventory-new-item-brand"
                          value={catalogItemForm.brand}
                          onChange={(event) =>
                            updateCatalogItemField("brand", event.target.value)
                          }
                          placeholder="Optional"
                          disabled={!canAddCatalogItemToSelection}
                        />
                      </div>
                    </div>
                    <div className="inventory-form__row">
                      <div className="inventory-field">
                        <label htmlFor="inventory-new-item-unit">Unit</label>
                        <input
                          id="inventory-new-item-unit"
                          value={catalogItemForm.unit}
                          onChange={(event) =>
                            updateCatalogItemField("unit", event.target.value)
                          }
                          disabled={!canAddCatalogItemToSelection}
                          required
                        />
                      </div>
                      <div className="inventory-field">
                        <label htmlFor="inventory-new-item-threshold">
                          Low-stock threshold
                        </label>
                        <input
                          id="inventory-new-item-threshold"
                          type="number"
                          min="0"
                          step="0.01"
                          value={catalogItemForm.low_stock_threshold}
                          onChange={(event) =>
                            updateCatalogItemField(
                              "low_stock_threshold",
                              event.target.value
                            )
                          }
                          placeholder="Default"
                          disabled={!canAddCatalogItemToSelection}
                        />
                      </div>
                    </div>
                    <div className="inventory-field">
                      <label htmlFor="inventory-new-item-notes">Notes</label>
                      <textarea
                        id="inventory-new-item-notes"
                        value={catalogItemForm.notes}
                        onChange={(event) =>
                          updateCatalogItemField("notes", event.target.value)
                        }
                        placeholder="Optional"
                        rows="3"
                        disabled={!canAddCatalogItemToSelection}
                      />
                    </div>
                    {workflowError.catalogItem ? (
                      <div className="inventory-inline-message inventory-inline-message--error">
                        {workflowError.catalogItem}
                      </div>
                    ) : null}
                    {workflowMessage.catalogItem ? (
                      <div className="inventory-inline-message inventory-inline-message--success">
                        {workflowMessage.catalogItem}
                      </div>
                    ) : null}
                    <div className="inventory-form__actions">
                      <button
                        type="submit"
                        className="inventory-action-button"
                        disabled={
                          !canAddCatalogItemToSelection ||
                          isSubmittingCatalogItem
                        }
                      >
                        {isSubmittingCatalogItem ? "Adding..." : "Add item"}
                      </button>
                    </div>
                  </form>
                </div>
                </section>

                <section className="inventory-panel inventory-panel--stacked">
                <div className="inventory-panel__header">
                  <div>
                    <p className="inventory-panel__kicker">Storage</p>
                    <h2>Locations</h2>
                  </div>
                  <span>{inventory.locations.length}</span>
                </div>
                <div className="inventory-list">
                  {inventory.locations.length ? (
                    inventory.locations.map((location) => (
                      <article
                        key={location.id}
                        className="inventory-list__item inventory-list__item--location"
                      >
                        <div>
                          <strong>{location.name}</strong>
                          <p>
                            {location.location_type}
                            {location.vehicle_identifier
                              ? ` | ${location.vehicle_identifier}`
                              : ""}
                          </p>
                        </div>
                        <div className="inventory-list__meta">
                          {location.is_default_receiving_location ? (
                            <span className="inventory-badge inventory-badge--accent">
                              Default receiving
                            </span>
                          ) : null}
                          <span
                            className={
                              location.is_active
                                ? "inventory-badge"
                                : "inventory-badge inventory-badge--muted"
                            }
                          >
                            {location.is_active ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </article>
                    ))
                  ) : (
                    <p className="inventory-empty">No locations found.</p>
                  )}
                </div>
                </section>
              </div>
            ) : null}

            {isCatalogLookupOpen ? (
              <section className="inventory-panel inventory-panel--table">
              <div className="inventory-panel__header">
                <div>
                  <p className="inventory-panel__kicker">Catalog lookup</p>
                  <h2>Items</h2>
                </div>
                <span>Showing {filteredItems.length}</span>
              </div>
              <div className="inventory-table-wrap">
                <table className="inventory-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Category</th>
                      <th>Brand</th>
                      <th>Unit</th>
                      <th>Low-stock threshold</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.length ? (
                      filteredItems.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <strong>{item.name}</strong>
                            <div className="inventory-table__subtext">
                              {item.display_path}
                            </div>
                          </td>
                          <td>{item.category?.path || "Uncategorized"}</td>
                          <td>{item.brand || "N/A"}</td>
                          <td>{item.unit}</td>
                          <td>
                            {item.low_stock_threshold
                              ? formatQuantity(item.low_stock_threshold)
                              : "Default"}
                          </td>
                          <td>{item.is_active ? "Active" : "Inactive"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="inventory-empty inventory-empty--table">
                          No catalog items match the current filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              </section>
            ) : null}

            <section className="inventory-panel inventory-panel--table inventory-panel--collapsed">
              <button
                type="button"
                className="inventory-panel__toggle"
                onClick={() =>
                  setIsStockByLocationOpen((currentIsOpen) => !currentIsOpen)
                }
                aria-expanded={isStockByLocationOpen}
              >
                <div>
                  <p className="inventory-panel__kicker">On hand</p>
                  <h2>Stock by location</h2>
                </div>
                <span>
                  {isStockByLocationOpen ? "Hide" : "Show"} {filteredStockRows.length}
                </span>
              </button>
              {isStockByLocationOpen ? (
                <div className="inventory-table-wrap">
                  <table className="inventory-table">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Location</th>
                        <th>Type</th>
                        <th>Qty on hand</th>
                        <th>Last cost</th>
                        <th>Threshold</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStockRows.length ? (
                        filteredStockRows.map((stockRow) => (
                          <tr key={stockRow.id}>
                            <td>
                              <strong>{stockRow.item?.name}</strong>
                              <div className="inventory-table__subtext">
                                {stockRow.item?.display_path || "N/A"}
                              </div>
                            </td>
                            <td>{stockRow.location?.name || "N/A"}</td>
                            <td>{stockRow.location?.location_type || "N/A"}</td>
                            <td>{formatQuantity(stockRow.quantity_on_hand)}</td>
                            <td>{formatCurrency(stockRow.last_unit_cost)}</td>
                            <td>
                              {formatQuantity(
                                stockRow.effective_low_stock_threshold
                              )}
                            </td>
                            <td>{formatCurrency(stockRow.stock_value)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="inventory-empty inventory-empty--table">
                            No stock rows match the current filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </section>

            <section className="inventory-page__reports">
              <article className="inventory-panel inventory-panel--reports">
                <div className="inventory-panel__header inventory-panel__header--workflow">
                  <div>
                    <p className="inventory-panel__kicker">Reports and exports</p>
                    <h2>Operational follow-up</h2>
                  </div>
                  <span>{inventory.lowStock.length} low-stock rows</span>
                </div>
                <p className="inventory-panel__description">
                  Export the current filtered view to CSV or open a clean printable
                  report for field and office follow-up.
                </p>
                <div className="inventory-report-actions">
                  <button
                    type="button"
                    className="inventory-action-button"
                    disabled={isDownloading.stock}
                    onClick={handleExportStock}
                  >
                    {isDownloading.stock ? "Exporting..." : "Export Stock CSV"}
                  </button>
                  <button
                    type="button"
                    className="inventory-action-button inventory-action-button--secondary"
                    disabled={isDownloading.transactions}
                    onClick={handleExportTransactions}
                  >
                    {isDownloading.transactions
                      ? "Exporting..."
                      : "Export Transactions CSV"}
                  </button>
                  <button
                    type="button"
                    className="inventory-action-button inventory-action-button--secondary"
                    onClick={handleOpenPrintableReport}
                  >
                    Print Inventory Report
                  </button>
                </div>
              </article>
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default InventoryPage;
